import {action, computed, flow, makeObservable, observable} from "mobx";
import {PromptDataStore} from "./PromptDataStore";
import {ErrorSeverityEnum, PromptManagementSystemPagesTypes} from "../types";
import {
  PROMPT_CREATION_PAGE_VALIDATION_CREATED_SUCCESS,
  PROMPT_CREATION_PAGE_VALIDATION_MISSING_CONTENT,
  PROMPT_CREATION_PAGE_VALIDATION_MISSING_NAME,
  PROMPT_CREATION_PAGE_VALIDATION_NAME_ALREADY_EXISTS,
  PROMPT_CREATION_PAGE_VALIDATION_UPDATED_SUCCESS,
  PROMPT_MODAL_CONFIRMATION_DELETE_SUCCESS,
} from "../PromptManagementSystemConstants";
import {API} from "aws-amplify";
import {deletePrompt, getPrompt, putPrompt, getAgent, putAgent} from "../../../../graphql/queries";

export class PromptManagementStore {
  currentPage = PromptManagementSystemPagesTypes.PAGE_LIST;
  listPrompt = [];
  currentPrompt = null;
  error = {
    show: false,
    severity: ErrorSeverityEnum.ERROR,
    title: "",
    message: "",
    type: ""
  }
  categories = [
    "Sales",
    "Inventory",
    "Service",
    "Operations",
    "Marketing",
    "Telemetry",
    "Analytics",
    "Finance",
    "HR"
  ];
  isLoadingAll = false;
  isLoadingOne = false;
  isCreating = false;
  isEditing = false;
  isDeleting = false;
  customer = null;
  confirmDelete = false;
  cantDelete = false;
  agentsInUse = [];
  agents = [];
  selectedAgent = {"label": "", "value": ""};

  constructor() {
    makeObservable(this,  {
      currentPage: observable,
      listPrompt: observable,
      currentPrompt: observable,
      isEditing: observable,
      isDeleting: observable,
      isCreating: observable,
      isLoadingOne: observable,
      isLoadingAll: observable,
      customer: observable,
      error: observable,
      confirmDelete: observable,
      cantDelete: observable,
      agentsInUse: observable,
      agents: observable,
      selectedAgent: observable,
      onChangePage: action,
      onValidateData: action,
      cancel: action,
      add: action,
      getOne: action,
      update: action,
      showAlert: action,
      closeAlert: action,
      onConfirmDelete: action,
      onChangeSelectedAgent: action,
      isLoading: computed,
      create: flow,
      getOne: flow,
      update: flow,
      all: flow,
      remove: flow,
      getAgents: flow,
      updateAgent: flow,
    });
  }
  
  get isLoading() {
    return this.isLoadingAll || this.isCreating || this.isEditing || this.isDeleting || this.isLoadingOne;
  }
  
  onChangePage(customer, page, id = "") {
    this.customer = customer;
    this.currentPage = page;
    this.error.type = "";

    if (page === PromptManagementSystemPagesTypes.PAGE_LIST) {
      void this.all();
    }

    if (page === PromptManagementSystemPagesTypes.PAGE_CREATE) {
      this.add();
    }

    if (page === PromptManagementSystemPagesTypes.PAGE_EDIT) {
      void this.getOne(id);
    }
  }
  
  onValidateData() {
    if (!this.currentPrompt.name) {
      this.showAlert({
        severity: ErrorSeverityEnum.ERROR,
        title: PROMPT_CREATION_PAGE_VALIDATION_MISSING_NAME.title,
        message: PROMPT_CREATION_PAGE_VALIDATION_MISSING_NAME.message
      });
      return false;
    }

    if (!this.currentPrompt.promptContent) {
      this.showAlert({
        severity: ErrorSeverityEnum.ERROR,
        title: PROMPT_CREATION_PAGE_VALIDATION_MISSING_CONTENT.title,
        message: PROMPT_CREATION_PAGE_VALIDATION_MISSING_CONTENT.message
      });
      return false;
    }
    
    return true;
  }

  add() {
    this.currentPrompt = new PromptDataStore();
  }
  
  cancel(){
    this.currentPrompt = null;
    void this.onChangePage(this.customer, PromptManagementSystemPagesTypes.PAGE_LIST);

    this.cantDelete = false;
    this.agentsInUse = [];
    this.confirmDelete = false;
  }
  
  onConfirmDelete() {
    this.confirmDelete = true;
    this.cantDelete = false;
    this.agentsInUse = [];
  }
  
  cancel(){
    this.currentPrompt = null;
    this.currentPage = 'list';
  }
  
  showAlert({ title, message, severity, type }) {
    this.error.show = true;
    this.error.title = title;
    this.error.message = message;
    this.error.severity = severity;
    this.error.type = type;
  }
  
  closeAlert() {
    this.error.show = false;
    this.error.title = "";
    this.error.message = "";
    this.error.severity = "error";
  }

  *remove(confirm) {
    this.confirmDelete = false;
    if (confirm) {
      try {
        this.isDeleting = true;
        
        const response = yield API.graphql({
          query: deletePrompt,
          variables: {
            input: {
              id: this.currentPrompt.id,
              customer_id: this.customer,
            },
          },
          authMode: "AMAZON_COGNITO_USER_POOLS",
        });

        const responseStatus = JSON.parse(response.data.deletePrompt.statusCode);
        
        if (responseStatus === 400) {
          const responseData = JSON.parse(response.data.deletePrompt.body);
          
          this.cantDelete = true;
          this.agentsInUse = responseData.agents;
          return;
        }

        this.showAlert({
          severity: ErrorSeverityEnum.SUCCESS,
          title: PROMPT_MODAL_CONFIRMATION_DELETE_SUCCESS.title,
          message: PROMPT_MODAL_CONFIRMATION_DELETE_SUCCESS.message
        });
                
        this.currentPrompt = null;

        void this.onChangePage(this.customer, PromptManagementSystemPagesTypes.PAGE_LIST);
      } catch (e) {
        this.showAlert({
          severity: ErrorSeverityEnum.ERROR,
          title: "Error",
          message: "An error occurred while deleting the prompt."
        });
      } finally {
        this.isDeleting = false;
      }
    }
  }
  
  *all() {
    try {
      this.listPrompt = [];
      this.isLoadingAll = true;

      const response = yield API.graphql({
        query: getPrompt,
        variables: {
          input: {
            user_id: this.customer,
          },
        },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });

      if (response.data.getPrompt.body !== "prompt not found") {
        const listResult = JSON.parse(response.data.getPrompt.body);

        this.listPrompt = listResult.map((item) => {
          const newPrompt = new PromptDataStore();
          newPrompt.setData({
            id: item.id,
            hash: item.commit_hash,
            name: item.name,
            shortDescription: item.description,
            promptContent: item.human_instruction,
            category: item.category,
            updatedAt: item.last_modified,
          });
          return newPrompt;
        });
      }
    } catch (e) {
      this.showAlert({
        severity: ErrorSeverityEnum.ERROR,
        title: "Error",
        message: "An error occurred while get list prompt"
      });
    } finally {
      this.isLoadingAll = false;
    }
  }
  
  *create() {
    try {
      this.isCreating = true;

      const isComplete = this.onValidateData();

      if (!isComplete) {
        this.isCreating = false;
        return;
      }

      const existingPrompt = this.listPrompt.find(prompt => prompt.name === this.currentPrompt.name);
      if (existingPrompt) {
        this.showAlert({
          severity: ErrorSeverityEnum.WARNING,
          title: PROMPT_CREATION_PAGE_VALIDATION_NAME_ALREADY_EXISTS.title,
          message: PROMPT_CREATION_PAGE_VALIDATION_NAME_ALREADY_EXISTS.message
        });
        return;
      }
      
      yield API.graphql({
        query: putPrompt,
        variables: {
          input: {
            category: this.currentPrompt.category || "",
            customer_id: this.customer,
            description: this.currentPrompt.shortDescription || "",
            human_instruction: this.currentPrompt.promptContent,
            name: this.currentPrompt.name,
          },
        },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });
      

      this.showAlert({
        severity: ErrorSeverityEnum.SUCCESS,
        title: PROMPT_CREATION_PAGE_VALIDATION_CREATED_SUCCESS.title,
        message: PROMPT_CREATION_PAGE_VALIDATION_CREATED_SUCCESS.message
      });
      
      this.currentPrompt = null;
      
      void this.onChangePage(this.customer, PromptManagementSystemPagesTypes.PAGE_LIST);
    } catch (e) {
      this.showAlert({
        severity: ErrorSeverityEnum.ERROR,
        title: "Error",
        message: "An error occurred while creating the prompt."
      });
    } finally {
      this.isCreating = false;
    }
  }

  *getOne(id) {
    try {
      this.isLoadingOne = true;
      
      const response = yield API.graphql({
        query: getPrompt,
        variables: {
          input: {
            id,
            user_id: this.customer,
          },
        },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });

      if (response.data.getPrompt.body === "prompt not found") {
        this.error.type = 'prompt_not_found';
        this.showAlert({
          severity: ErrorSeverityEnum.ERROR,
          title: "Prompt not found",
          message: "The prompt you are looking for does not exist."
        });
        return;
      }

      const item = JSON.parse(response.data.getPrompt.body)[0];

      const newPrompt = new PromptDataStore();
      newPrompt.setData({
        id: item.id,
        hash: item.commit_hash,
        name: item.name,
        shortDescription: item.description,
        promptContent: item.human_instruction,
        category: item.category,
        updatedAt: item.last_modified,
      });

      this.currentPrompt = newPrompt;
    } catch (e){
      this.showAlert({
        severity: ErrorSeverityEnum.ERROR,
        title: "Error",
        message: "An error occurred while getting the prompt."
      });
    } finally {
      this.isLoadingOne = false;
    }
  }

  *update() {
    try {
      this.isEditing = true;

      const isComplete = this.onValidateData();

      if (!isComplete) {
        this.isCreating = false;
        return;
      }

      yield API.graphql({
        query: putPrompt,
        variables: {
          input: {
            id: this.currentPrompt.id,
            commit_hash: this.currentPrompt.hash,
            category: this.currentPrompt.category || "",
            customer_id: this.customer,
            description: this.currentPrompt.shortDescription || "",
            human_instruction: this.currentPrompt.promptContent,
            name: this.currentPrompt.name,
          },
        },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });

      const index = this.listPrompt.findIndex(prompt => prompt.id === this.currentPrompt.id);
      this.listPrompt[index] = this.currentPrompt;


      const agent = this.agents.find(agent => agent.id === this.selectedAgent.value);
      if (agent) {
        yield this.updateAgent(agent);
      }

      this.showAlert({
        severity: ErrorSeverityEnum.SUCCESS,
        title: PROMPT_CREATION_PAGE_VALIDATION_UPDATED_SUCCESS.title,
        message: PROMPT_CREATION_PAGE_VALIDATION_UPDATED_SUCCESS.message
      });
    } catch (e) {
      this.showAlert({
        severity: ErrorSeverityEnum.ERROR,
        title: "Error",
        message: "An error occurred while updating the prompt."
      });
    } finally {
      this.isEditing = false;
    }
  }

  *getAgents() {
    try {
      this.selectedAgent = {"label": "", "value": ""};
      const response = yield API.graphql({
        query: getAgent,
        variables: {
          input: {
            customer_id: this.customer,
          },
        },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });

      const agents = JSON.parse(response.data.getAgent.body);
      this.agents = agents.filter(agent => agent.type === 'langchain');
    } catch (error) {
      this.showAlert({
        severity: ErrorSeverityEnum.ERROR,
        title: "Error",
        message: "An error occurred while getting the agents."
      });
    }
  }

  *updateAgent(agent) {
    try{
      const agentInput = {
        id: agent.id,
        name: agent.name,
        customer_id: agent.customer_id,
        human_instructions: agent.human_instructions,
        tone: agent.tone,
        conversation_starters: agent.conversation_starters,
        job_title: agent.job_title,
        channels: JSON.stringify(agent.channels),
        knowledge_base: agent.knowledge_base,
        persona: JSON.stringify(agent.persona),
        prompt_id: this.currentPrompt.id,
        scope: agent.scope,
        use_prompter: agent.use_prompter || false,
        add_to_concierge: agent.add_to_concierge,
        extended_answer: agent.extended_answer,
        actions: agent.actions,
        prompt: agent.prompt,
        domain: agent.domain,
        photo_url: agent.photo_url,
        signature: JSON.stringify(agent.signature),
        type: agent.type,
        category: agent.category,
        model: agent.model,
      }
  
      yield API.graphql({
        query: putAgent,
        variables: { input: agentInput },
        authMode: 'AMAZON_COGNITO_USER_POOLS'
      });
      return
    } catch (error) {
      if (
        error.errors.length &&
        error.errors[0].errorType === "Lambda:ExecutionTimeoutException"
      ) {
        //Wait 10secs in case this endpoint time outs (it times out because of Appsync's 30 seconds limit)
        yield new Promise((resolve) => setTimeout(resolve, 10000));
        return
      }
      this.showAlert({
        severity: ErrorSeverityEnum.ERROR,
        title: "Error",
        message: "An error occurred while updating the agent."
      });
    }

  }

  onChangeSelectedAgent = (agent) => {
    this.selectedAgent = agent;
  }
}

export const PromptManagementStoreInstance = new PromptManagementStore();
